/**
 * InlineCascadePicker — R3 (SPEC invoice-identity-correctable) — G1 / AC1 / AC2 / AC7.
 *
 * The BINARY persistence choice Christa actually uses. When she corrects a BILL
 * TO field, this expands IN PLACE (no modal, no kebab, no hover) with exactly
 * TWO always-visible radios in HER words:
 *
 *   ( ) Just this invoice          → Fix only invoice 0602
 *   ( ) This project, going forward → Fix 0602 and all future Marine Landing invoices
 *
 * Christa's mental model is BINARY: "just this invoice" vs "this project ...
 * moving forward" (her literal words, Inv 520/586). She never asks for
 * client-wide-but-not-this-project, so the client level is NOT a prominent third
 * radio — it is BURIED behind a de-emphasized disclosure, offered only on the
 * rare genuinely-multi-client project.
 *
 * Christa-psychology bar honored here (state doc G1):
 *   - "Just this invoice" is ALWAYS the default selection (AC7) — the scary
 *     "change everything" is never the only path, and a fast Save can never
 *     silently rewrite siblings.
 *   - CONSEQUENCE stated in her words with the ACTUAL invoice number + project
 *     name (AC2), not a system scope name or a bare count.
 *   - ALWAYS-VISIBLE controls (radios), no hover-only, no kebab (she called a
 *     kebab "too much going on there").
 *   - NO user-facing string contains scope / establishment / default /
 *     field_update_mode / propagate (AC1). `assertNoSystemVocab` with the
 *     stricter persistence-choice list is the runtime guard a test runs over
 *     every produced label + consequence.
 *
 * This component is presentation + selection only. It does NOT perform the
 * network write; the parent's onSave(level) does (so the parent owns the
 * evidentiary confirmation generated from the actual saved value). This keeps
 * the picker deterministically testable.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  assertNoSystemVocab,
  formatInvoiceNumber,
  PERSISTENCE_CHOICE_FORBIDDEN,
} from './billToSourceTag';

/** R3 cascade levels, mirrored on the FE (string-equal to the backend enum). */
export type CascadeLevel = 'this_invoice' | 'this_project' | 'this_client';

/**
 * Why the field is being edited. Retained for signature compatibility with
 * existing callers; R3 no longer varies the DEFAULT selection by reason (the
 * one-off is ALWAYS the default per AC7), so this is now advisory only.
 */
export type PickerEditReason = 'unresolved' | 'pulled_unconfirmed' | 'overriding';

export interface InlineCascadePickerProps {
  /** Human field label for headings, plain-language ("DBA", "Billing address"). */
  fieldLabel: string;
  /** Client display name — used in the buried client-level consequence copy. */
  clientName: string | null | undefined;
  /** Project display name — used in the "going forward" consequence preview. */
  projectName: string | null | undefined;
  /**
   * The zero-padded invoice number this correction is being made ON. Rendered
   * VERBATIM in the consequence copy ("Fix only invoice 0602"). Optional —
   * falls back to generic "this invoice" copy when absent.
   */
  invoiceNumber?: string | null;
  /**
   * Whether this project genuinely bills MORE THAN ONE client. Only then is the
   * BURIED client-level option offered (she never wants client-wide-but-not-
   * this-project on a single-client project — it would be identical to "this
   * project").
   */
  multiClientProject: boolean;
  /** Advisory only in R3 (see PickerEditReason). Kept for caller compatibility. */
  editReason?: PickerEditReason;
  /** Whether the THIS-INVOICE option is offered (always true in practice). */
  allowThisInvoice?: boolean;
  /**
   * Does this invoice have a client scope for a project-level default to land
   * on? A project write with no scope RAISES (AC5 no-silent-no-op), so when
   * this is false the picker falls back to pre-selecting the one-off rather
   * than defaulting to a Save that would fail.
   */
  hasProjectScope?: boolean;
  /**
   * True when this Save carries a hide-DBA flip.
   *
   * Surfaces the CLIENT level on a single-client project, which is otherwise
   * unreachable. Her 2026-06-22 ask (inv 618) is client-wide -- "Living Balance
   * does not like being tagged to any of their projects - just a client" -- and
   * Living Balance is a single-client project, so the multiClientProject gate
   * hid the only level she actually wanted.
   *
   * Narrow on purpose. It does NOT relax the burial for a DBA/legal-entity
   * change: those are retroactive and sibling-destroying, which is what she
   * objected to in "it says override all for this client" (2026-05-28).
   * hide_dba sets a display default for FUTURE invoices and rewrites nothing.
   */
  hidingDba?: boolean;
  /** Save handler — receives the chosen cascade level. Parent does the write. */
  onSave: (level: CascadeLevel) => void;
  /** Cancel handler — closes the picker with no write. */
  onCancel: () => void;
  /** Optional extra body slotted above the radios (e.g. the DBA <select>). */
  children?: React.ReactNode;
  /** Disable Save (e.g. no value chosen yet). */
  saveDisabled?: boolean;
  // -- deprecated R4 props kept so existing callers/tests still type-check --
  /** @deprecated R4 count copy removed — binary labels are in her words now. */
  projectDraftCount?: number;
  /** @deprecated R4 count copy removed — binary labels are in her words now. */
  clientInvoiceCount?: number;
}

export interface PickerOption {
  level: CascadeLevel;
  /** The short radio label in her words. */
  label: string;
  /** The plain-language consequence with the ACTUAL invoice/project (AC2). */
  consequence: string;
  testId: string;
}

/**
 * Build the BINARY persistence options + their plain-language consequence copy.
 * Exported for direct unit testing of the AC2 copy without a DOM render.
 *
 * Returns exactly two options: "Just this invoice" (default) and "This project,
 * going forward". The client-level option is intentionally NOT here — it is
 * buried (see `buildBuriedClientOption`).
 */
export function buildPickerOptions(args: {
  projectName: string | null | undefined;
  invoiceNumber?: string | null;
  allowThisInvoice?: boolean;
}): PickerOption[] {
  const { projectName, invoiceNumber, allowThisInvoice = true } = args;

  const project = projectName?.trim() || 'this project';
  const num = formatInvoiceNumber(invoiceNumber);
  const options: PickerOption[] = [];

  if (allowThisInvoice) {
    options.push({
      level: 'this_invoice',
      label: 'Just this invoice',
      // "Fix only invoice 0602" (AC2). No number → generic.
      consequence: num ? `Fix only invoice ${num}` : 'Fix only this invoice',
      testId: 'cascade-option-this_invoice',
    });
  }

  options.push({
    level: 'this_project',
    label: 'This project, going forward',
    // "Fix 0602 and all future Marine Landing invoices" (AC2, her words).
    consequence: num
      ? `Fix ${num} and all future ${project} invoices`
      : `Fix this and all future ${project} invoices`,
    testId: 'cascade-option-this_project',
  });

  for (const opt of options) {
    assertNoSystemVocab(opt.label, 'cascade option label', PERSISTENCE_CHOICE_FORBIDDEN);
    assertNoSystemVocab(opt.consequence, 'cascade option consequence', PERSISTENCE_CHOICE_FORBIDDEN);
  }
  return options;
}

/**
 * The BURIED client-level option (AC1: "not a prominent third radio"). Offered
 * only on a genuinely-multi-client project, behind a de-emphasized disclosure.
 */
export function buildBuriedClientOption(args: {
  clientName: string | null | undefined;
  invoiceNumber?: string | null;
}): PickerOption {
  const { clientName, invoiceNumber } = args;
  const client = clientName?.trim() || 'this client';
  const num = formatInvoiceNumber(invoiceNumber);
  const option: PickerOption = {
    level: 'this_client',
    label: `This client, on every project`,
    consequence: num
      ? `Fix ${num} and every future ${client} invoice, on any project`
      : `Fix this and every future ${client} invoice, on any project`,
    testId: 'cascade-option-this_client',
  };
  assertNoSystemVocab(option.label, 'buried client option label', PERSISTENCE_CHOICE_FORBIDDEN);
  assertNoSystemVocab(option.consequence, 'buried client option consequence', PERSISTENCE_CHOICE_FORBIDDEN);
  return option;
}

/**
 * The default level (AC7).
 *
 * 2026-08-07: this returned 'this_invoice' UNCONDITIONALLY. The stated reason
 * was that "the scary change-everything is never the only path and a fast Save
 * can never silently rewrite siblings" — but the sibling-rewriting hazard was
 * removed separately (B2: a going-forward save now fixes the current invoice and
 * sets the future default, and rewrites no sibling drafts — see BillToPanel's
 * `affectedNumbers: []`). The default it justified was never revisited.
 *
 * What that cost, measured on production 2026-08-07: 6 of 317 client scopes
 * carry a project default, and 0 of 566 orgs carry a client default. Almost
 * every identity correction Christa has ever made was a one-off, because a
 * one-off is what the pre-selected option produces and she is working a backlog.
 * So the same entity came back wrong the following month, every month:
 *
 *   2026-06-23 (inv 609): "I changed it last time from Leah - to made by pacific
 *   and it's gone back to Leah... thought it stuck but guess not!!"
 *   2026-07-15 (inv 635): "it said it would only apply to that invoice. I need it
 *   to apply to all invoices moving forward for that project."
 *
 * "This project, going forward" is the option she has asked for by hand four
 * separate times, so it is the default. "Just this invoice" stays available for
 * the genuine one-off (2026-04-10, inv 482) — it is now a deliberate choice
 * rather than the thing that happens when she saves quickly.
 *
 * `hasProjectScope` is the one guard rail: a project-level write needs a client
 * scope to land on, and raises if there is none (AC5's no-silent-no-op). An
 * invoice without one falls back to the one-off so Save cannot fail by default.
 */
export function defaultLevelForReason(
  _reason?: PickerEditReason,
  _multiClientProject?: boolean,
  hasProjectScope: boolean = true,
): CascadeLevel {
  return hasProjectScope ? 'this_project' : 'this_invoice';
}

export function InlineCascadePicker({
  fieldLabel,
  clientName,
  projectName,
  invoiceNumber,
  multiClientProject,
  allowThisInvoice = true,
  hasProjectScope = true,
  hidingDba = false,
  onSave,
  onCancel,
  children,
  saveDisabled = false,
}: InlineCascadePickerProps) {
  const options = useMemo(
    () => buildPickerOptions({ projectName, invoiceNumber, allowThisInvoice }),
    [projectName, invoiceNumber, allowThisInvoice],
  );
  const buriedClientOption = useMemo(
    () => buildBuriedClientOption({ clientName, invoiceNumber }),
    [clientName, invoiceNumber],
  );

  // AC7 — pre-select the level that STICKS (see defaultLevelForReason). Sourced
  // from the same function the tests assert on, so the component and the
  // contract can never drift.
  // `|| allowThisInvoice === false`: when the one-off is not offered at all,
  // this_project is the only option, so it must be the default even with no
  // scope — otherwise we would pre-select a level the user cannot see.
  const [selected, setSelected] = useState<CascadeLevel>(() =>
    defaultLevelForReason(
      undefined,
      multiClientProject,
      hasProjectScope || allowThisInvoice === false,
    ),
  );
  // AC1 — the client level is buried behind a de-emphasized disclosure.
  const [clientRevealed, setClientRevealed] = useState(false);

  // 2026-08-09: the client radio can UNMOUNT while still selected. She ticks
  // "Hide DBA on the PDF", picks "This client, on every project", then changes
  // her mind and un-ticks. On a single-client project `hidingDba` was the only
  // thing revealing that option, so it vanishes -- leaving no radio visibly
  // checked while `selected` was still 'this_client'. Save then wrote at client
  // level, which is not what the screen showed.
  //
  // That is the exact divergence this picker exists to prevent, so fall back to
  // the default level the moment the option stops being reachable.
  const clientReachable = (multiClientProject || hidingDba) && (clientRevealed || hidingDba);
  useEffect(() => {
    if (selected === 'this_client' && !clientReachable) {
      setSelected(
        defaultLevelForReason(
          undefined,
          multiClientProject,
          hasProjectScope || allowThisInvoice === false,
        ),
      );
    }
  }, [clientReachable, selected, multiClientProject, hasProjectScope, allowThisInvoice]);

  return (
    <div className="space-y-2" data-testid="inline-cascade-picker">
      {children}
      <fieldset
        className="flex flex-col gap-2 text-sm"
        data-testid="cascade-options"
      >
        <legend className="sr-only">{`Apply this ${fieldLabel} change to…`}</legend>
        {options.map((opt) => (
          <label
            key={opt.level}
            className="flex items-start gap-2 cursor-pointer"
          >
            <input
              type="radio"
              name={`persistence-${fieldLabel}`}
              value={opt.level}
              checked={selected === opt.level}
              onChange={() => setSelected(opt.level)}
              data-testid={opt.testId}
              className="mt-0.5"
            />
            <span className="flex flex-col">
              <span className="font-medium text-slate-900">{opt.label}</span>
              <span
                className="text-slate-600"
                data-testid={`${opt.testId}-consequence`}
              >
                {opt.consequence}
              </span>
            </span>
          </label>
        ))}

        {/* AC1 — BURIED client level. Only on genuinely-multi-client projects,
            behind a de-emphasized text disclosure — never a prominent third
            radio. Christa never asks for client-wide-but-not-this-project. */}
        {(multiClientProject || hidingDba) &&
          (clientRevealed || hidingDba ? (
            <label className="flex items-start gap-2 cursor-pointer pl-1">
              <input
                type="radio"
                name={`persistence-${fieldLabel}`}
                value={buriedClientOption.level}
                checked={selected === buriedClientOption.level}
                onChange={() => setSelected(buriedClientOption.level)}
                data-testid={buriedClientOption.testId}
                className="mt-0.5"
              />
              <span className="flex flex-col">
                <span className="font-medium text-slate-700">
                  {buriedClientOption.label}
                </span>
                <span
                  className="text-slate-500"
                  data-testid={`${buriedClientOption.testId}-consequence`}
                >
                  {buriedClientOption.consequence}
                </span>
              </span>
            </label>
          ) : (
            <button
              type="button"
              className="self-start text-xs text-slate-400 hover:text-slate-600 underline"
              data-testid="cascade-more-options"
              onClick={() => setClientRevealed(true)}
            >
              More than one client on this project?
            </button>
          ))}
      </fieldset>
      <div className="flex gap-2 justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          data-testid="cascade-picker-cancel"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => onSave(selected)}
          disabled={saveDisabled}
          data-testid="cascade-picker-save"
        >
          Save
        </Button>
      </div>
    </div>
  );
}
